<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import pipelineAutomationsAPI from 'dashboard/api/pipelineAutomations';
import Button from 'dashboard/components-next/button/Button.vue';
import Modal from 'dashboard/components/Modal.vue';
import { default as FluentIcon } from 'shared/components/FluentIcon/Icon.vue';
import dashboardIcons from 'shared/components/FluentIcon/dashboard-icons.json';
import { usePipelinePermissions } from '../composables/usePipelinePermissions';
import { normalizeAutomationFlow } from '../automations/utils/triggerFilterContract';

const emit = defineEmits(['create', 'edit', 'templates', 'history']);

const { t } = useI18n();
const { showAlert } = useAlert();
const automations = ref([]);
const loading = ref(true);
const loadError = ref(false);
const togglingId = ref(null);
const exportingId = ref(null);
const duplicatingId = ref(null);
const isImporting = ref(false);
const importFileInput = ref(null);

// Account-aware admin check (respects custom roles) — not a raw role string,
// which ignored custom-role base-permission divergence (audit P2-8).
const { isAdmin: userIsAdmin } = usePipelinePermissions();

const fetchAutomations = async () => {
  loading.value = true;
  loadError.value = false;
  try {
    const { data } = await pipelineAutomationsAPI.get();
    automations.value = Array.isArray(data) ? data : [];
  } catch {
    automations.value = [];
    loadError.value = true;
    showAlert(t('PIPELINE.AUTOMATIONS.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

onMounted(fetchAutomations);

const handleToggle = async automation => {
  if (!userIsAdmin.value || togglingId.value === automation.id) return;
  togglingId.value = automation.id;
  const originalActive = automation.active;
  try {
    await pipelineAutomationsAPI.update(automation.id, {
      active: !originalActive,
    });
    automation.active = !originalActive;
  } catch {
    // Revert is automatic since we only flip after a successful request.
    showAlert(t('PIPELINE.AUTOMATIONS.TOGGLE_ERROR'));
  } finally {
    togglingId.value = null;
  }
};

const triggerIconMap = {
  event: 'i-lucide-zap',
  scheduled: 'i-lucide-clock',
  webhook: 'i-lucide-webhook',
  manual: 'i-lucide-mouse-pointer-click',
};

const accentColor = automation => {
  if (!automation.active) return 'bg-n-slate-6';
  const status = automation.stats?.last_execution_status;
  if (!automation.stats?.total_executions) return 'bg-n-amber-8';
  if (status === 'completed') return 'bg-n-teal-9';
  if (status === 'failed') return 'bg-n-ruby-9';
  return 'bg-n-slate-6';
};

const relativeTime = dateStr => {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)
    return t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.TIME_JUST_NOW');
  if (mins < 60)
    return t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.TIME_MINUTES_AGO', {
      count: mins,
    });
  const hours = Math.floor(mins / 60);
  if (hours < 24)
    return t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.TIME_HOURS_AGO', {
      count: hours,
    });
  return t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.TIME_DAYS_AGO', {
    count: Math.floor(hours / 24),
  });
};

const formatAvgTime = ms => {
  if (!ms) return '';
  const secs = (ms / 1000).toFixed(1);
  return `~${secs}s`;
};

// Delete confirmation uses the shared accessible modal. State is local;
// handleDelete opens it and performDelete confirms the destructive action.
const automationToDelete = ref(null);

const handleDelete = automation => {
  if (!userIsAdmin.value) return;
  automationToDelete.value = automation;
};

const dismissDelete = () => {
  automationToDelete.value = null;
};

const performDelete = async () => {
  if (!userIsAdmin.value) return;
  const automation = automationToDelete.value;
  if (!automation) return;
  automationToDelete.value = null;
  try {
    await pipelineAutomationsAPI.destroy(automation.id);
    automations.value = automations.value.filter(a => a.id !== automation.id);
    showAlert(t('PIPELINE.FLOW_BUILDER.DELETE_SUCCESS'));
  } catch {
    showAlert(t('PIPELINE.FLOW_BUILDER.DELETE_ERROR'));
  }
};

const handleExport = async automation => {
  if (!userIsAdmin.value || exportingId.value === automation.id) return;
  exportingId.value = automation.id;
  try {
    const { data } = await pipelineAutomationsAPI.exportAutomation(
      automation.id
    );
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeName = (automation.name || 'automation')
      .replace(/[^a-z0-9_-]/gi, '_')
      .toLowerCase();
    link.href = url;
    link.download = `${safeName}_automation.json`;
    link.click();
    URL.revokeObjectURL(url);
    showAlert(t('PIPELINE.FLOW_BUILDER.EXPORT_SUCCESS'));
  } catch {
    showAlert(t('PIPELINE.FLOW_BUILDER.EXPORT_ERROR'));
  } finally {
    exportingId.value = null;
  }
};

const handleDuplicate = async automation => {
  if (!userIsAdmin.value || duplicatingId.value === automation.id) return;
  duplicatingId.value = automation.id;
  try {
    const { data } = await pipelineAutomationsAPI.duplicate(automation.id);
    // Insert the clone right after the original so the user sees it without
    // scrolling/refetching. The backend returns the full automation payload
    // (including stats: zeroed for a fresh clone).
    const idx = automations.value.findIndex(a => a.id === automation.id);
    if (idx === -1) {
      automations.value.unshift(data);
    } else {
      automations.value.splice(idx + 1, 0, data);
    }
    showAlert(t('PIPELINE.FLOW_BUILDER.DUPLICATE_SUCCESS'));
  } catch {
    showAlert(t('PIPELINE.FLOW_BUILDER.DUPLICATE_ERROR'));
  } finally {
    duplicatingId.value = null;
  }
};

const openImportDialog = () => {
  if (!userIsAdmin.value) return;
  importFileInput.value?.click();
};

const handleImportFile = async event => {
  if (!userIsAdmin.value) return;
  const file = event.target.files?.[0];
  if (!file) return;
  // Reset input so same file can be re-selected
  event.target.value = '';

  isImporting.value = true;
  try {
    const text = await file.text();
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      showAlert(t('PIPELINE.FLOW_BUILDER.IMPORT_INVALID_JSON'));
      return;
    }

    // Accept both wrapped { automation: {...} } and bare export format
    const rawAutomationData = parsed.automation ?? parsed;
    const automationData =
      rawAutomationData && typeof rawAutomationData === 'object'
        ? {
            ...rawAutomationData,
            flow: normalizeAutomationFlow(rawAutomationData.flow),
          }
        : rawAutomationData;

    const { data } =
      await pipelineAutomationsAPI.importAutomation(automationData);
    showAlert(t('PIPELINE.FLOW_BUILDER.IMPORT_SUCCESS'));
    emit('edit', data.id);
  } catch (error) {
    const msg =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      t('PIPELINE.FLOW_BUILDER.IMPORT_ERROR');
    showAlert(msg);
  } finally {
    isImporting.value = false;
  }
};
</script>

<template>
  <div
    data-testid="flow-builder-page"
    class="flex flex-col h-full w-full min-w-0 overflow-x-hidden bg-n-background"
  >
    <!-- Header -->
    <header
      data-testid="flow-builder-header"
      class="flex flex-col items-stretch gap-4 p-4 border-b border-n-weak sm:flex-row sm:items-start sm:justify-between sm:p-6"
    >
      <div class="min-w-0">
        <h1 class="text-heading-1 text-n-slate-12 break-words">
          {{ t('PIPELINE.FLOW_BUILDER.TITLE') }}
        </h1>
        <p class="text-label-small text-n-slate-11 mt-1 break-words">
          {{ t('PIPELINE.FLOW_BUILDER.SUBTITLE') }}
        </p>
      </div>
      <div
        data-testid="flow-builder-header-actions"
        class="flex flex-col items-stretch gap-2 w-full sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end"
      >
        <!-- Hidden file input for import -->
        <input
          ref="importFileInput"
          type="file"
          accept=".json"
          class="hidden"
          @change="handleImportFile"
        />

        <Button
          v-if="userIsAdmin"
          variant="ghost"
          color="slate"
          class="min-h-11 w-full sm:w-auto"
          @click="emit('templates')"
        >
          <template #icon>
            <FluentIcon icon="apps" size="18" :icons="dashboardIcons" />
          </template>
          {{ t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.TEMPLATES_BUTTON') }}
        </Button>
        <Button
          v-if="userIsAdmin"
          variant="ghost"
          color="slate"
          class="min-h-11 w-full sm:w-auto"
          :disabled="isImporting"
          @click="openImportDialog"
        >
          <template #icon>
            <span class="i-lucide-upload size-4" />
          </template>
          {{
            isImporting
              ? t('PIPELINE.FLOW_BUILDER.IMPORTING')
              : t('PIPELINE.FLOW_BUILDER.IMPORT')
          }}
        </Button>
        <Button
          v-if="userIsAdmin"
          variant="solid"
          color="blue"
          class="min-h-11 w-full sm:w-auto"
          @click="emit('create')"
        >
          <template #icon>
            <FluentIcon icon="add" size="20" :icons="dashboardIcons" />
          </template>
          {{ t('PIPELINE.AUTOMATIONS.ADD') }}
        </Button>
      </div>
    </header>

    <!-- Content -->
    <div class="flex-1 min-w-0 overflow-y-auto p-4 sm:p-6">
      <!-- Section heading for screen readers — bridges the h1 -> card h3 gap -->
      <h2 class="sr-only">{{ t('PIPELINE.FLOW_BUILDER.LIST_HEADING') }}</h2>
      <!-- Loading skeleton -->
      <div
        v-if="loading"
        role="status"
        :aria-label="t('PIPELINE.AUTOMATIONS.LOADING')"
        class="grid grid-cols-1 xl:grid-cols-2 gap-4"
      >
        <div
          v-for="n in 4"
          :key="n"
          class="rounded-xl outline outline-1 outline-n-weak bg-n-solid-2 p-4 sm:p-5 animate-pulse flex gap-2 min-w-0"
        >
          <div class="w-1 bg-n-alpha-2 rounded self-stretch flex-shrink-0" />
          <div class="flex-1 min-w-0 space-y-3">
            <div class="h-4 bg-n-alpha-2 rounded w-2/3" />
            <div class="h-3 bg-n-alpha-2 rounded w-1/2" />
            <div class="flex gap-3">
              <div class="h-3 bg-n-alpha-2 rounded w-20" />
              <div class="h-3 bg-n-alpha-2 rounded w-16" />
              <div class="h-3 bg-n-alpha-2 rounded w-12" />
            </div>
            <div class="h-8 bg-n-alpha-2 rounded w-1/3" />
          </div>
        </div>
      </div>

      <!-- Error state stays distinct from a valid empty list. -->
      <div
        v-else-if="loadError"
        role="alert"
        data-testid="automations-load-error"
        class="flex min-h-96 flex-col items-center justify-center gap-4 px-4 text-center"
      >
        <span
          class="i-lucide-circle-alert size-10 text-n-ruby-9"
          aria-hidden="true"
        />
        <div class="max-w-md">
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('PIPELINE.AUTOMATIONS.LOAD_ERROR_TITLE') }}
          </h3>
          <p class="mt-2 text-sm text-n-slate-11">
            {{ t('PIPELINE.AUTOMATIONS.LOAD_ERROR') }}
          </p>
        </div>
        <Button
          variant="solid"
          color="blue"
          size="lg"
          class="min-h-11"
          @click="fetchAutomations"
        >
          <template #icon>
            <span class="i-lucide-refresh-cw size-4" aria-hidden="true" />
          </template>
          {{ t('PIPELINE.AUTOMATIONS.RETRY') }}
        </Button>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!automations.length"
        class="flex flex-col items-center justify-center min-h-96 text-center"
      >
        <div
          class="flex items-center justify-center w-20 h-20 mb-6 rounded-2xl bg-n-alpha-1"
        >
          <span class="i-lucide-workflow size-10 text-n-slate-8" />
        </div>
        <h3 class="text-heading-3 text-n-slate-12 mb-2">
          {{ t('PIPELINE.AUTOMATIONS.EMPTY_TITLE') }}
        </h3>
        <p class="text-n-slate-11 max-w-md mb-6">
          {{ t('PIPELINE.AUTOMATIONS.EMPTY_DESCRIPTION') }}
        </p>
        <div
          class="flex flex-col items-stretch gap-3 w-full max-w-sm sm:w-auto sm:max-w-none sm:flex-row sm:items-center"
        >
          <Button
            v-if="userIsAdmin"
            variant="ghost"
            color="slate"
            size="lg"
            class="min-h-11 w-full sm:w-auto"
            @click="emit('templates')"
          >
            <template #icon>
              <FluentIcon icon="apps" size="20" :icons="dashboardIcons" />
            </template>
            {{ t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.TEMPLATES_BUTTON') }}
          </Button>
          <Button
            v-if="userIsAdmin"
            variant="solid"
            color="blue"
            size="lg"
            class="min-h-11 w-full sm:w-auto"
            @click="emit('create')"
          >
            <template #icon>
              <FluentIcon icon="add" size="20" :icons="dashboardIcons" />
            </template>
            {{ t('PIPELINE.AUTOMATIONS.ADD') }}
          </Button>
        </div>
      </div>

      <!-- Automations Grid -->
      <div v-else class="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div
          v-for="automation in automations"
          :key="automation.id"
          class="relative flex overflow-hidden rounded-xl outline outline-1 transition-[box-shadow,opacity] duration-200"
          :class="
            automation.active
              ? 'outline-n-weak bg-n-solid-2 hover:shadow-md'
              : 'outline-n-weak bg-n-solid-2 opacity-70'
          "
        >
          <!-- Left accent bar -->
          <div
            class="w-1.5 flex-shrink-0 self-stretch rounded-r"
            :class="accentColor(automation)"
          />

          <!-- Card body -->
          <div class="flex-1 min-w-0 p-4 sm:p-5 flex flex-col gap-3">
            <!-- Header row: icon + name + badge + toggle -->
            <div
              data-testid="automation-card-header"
              class="flex flex-wrap items-start justify-between gap-3"
            >
              <div class="flex items-start gap-2 flex-1 basis-40 min-w-0">
                <span
                  class="size-4 text-n-slate-9 flex-shrink-0"
                  :class="[
                    triggerIconMap[automation.trigger_type] || 'i-lucide-zap',
                  ]"
                  aria-hidden="true"
                />
                <h3
                  class="text-sm font-semibold text-n-slate-12 break-words min-w-0"
                  :title="automation.name"
                >
                  {{ automation.name }}
                </h3>
              </div>
              <div class="flex items-center gap-2 flex-shrink-0">
                <span
                  class="px-2 py-0.5 text-xs font-medium rounded-full"
                  :class="
                    automation.active
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-alpha-2 text-n-slate-11'
                  "
                >
                  {{
                    automation.active
                      ? t('PIPELINE.AUTOMATIONS.STATUS.ACTIVE')
                      : t('PIPELINE.AUTOMATIONS.STATUS.INACTIVE')
                  }}
                </span>
                <!-- Toggle -->
                <button
                  role="switch"
                  :aria-checked="automation.active ? 'true' : 'false'"
                  :aria-label="
                    t(
                      automation.active
                        ? 'PIPELINE.FLOW_BUILDER.TOGGLE_OFF_ARIA'
                        : 'PIPELINE.FLOW_BUILDER.TOGGLE_ON_ARIA',
                      { name: automation.name }
                    )
                  "
                  class="relative inline-flex size-11 flex-shrink-0 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-1"
                  :class="[
                    togglingId === automation.id || !userIsAdmin
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer hover:bg-n-alpha-1',
                  ]"
                  :disabled="togglingId === automation.id || !userIsAdmin"
                  @click="handleToggle(automation)"
                >
                  <span
                    class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
                    :class="automation.active ? 'bg-n-brand' : 'bg-n-alpha-2'"
                    aria-hidden="true"
                  >
                    <span
                      class="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform"
                      :class="
                        automation.active
                          ? 'translate-x-[18px]'
                          : 'translate-x-0.5'
                      "
                    />
                  </span>
                </button>
              </div>
            </div>

            <!-- Description -->
            <p
              v-if="automation.description"
              data-testid="automation-description"
              class="text-xs text-n-slate-11 break-words sm:line-clamp-2"
            >
              {{ automation.description }}
            </p>

            <!-- Stats zone -->
            <div v-if="automation.stats" class="border-t border-n-weak pt-3">
              <!-- Never executed -->
              <p
                v-if="!automation.stats.total_executions"
                class="text-xs text-n-slate-9 flex items-center gap-1.5"
              >
                <span class="i-lucide-calendar-x size-3.5" aria-hidden="true" />
                {{ t('PIPELINE.FLOW_BUILDER.NEVER_EXECUTED') }}
              </p>

              <!-- Has executions -->
              <div v-else class="space-y-2">
                <!-- Stats row 1 -->
                <div
                  data-testid="automation-stats-row"
                  class="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-n-slate-11"
                >
                  <span class="flex items-center gap-1 min-w-0 break-words">
                    <span
                      class="i-lucide-play-circle size-3.5"
                      aria-hidden="true"
                    />
                    {{ automation.stats.total_executions }}
                    {{ t('PIPELINE.FLOW_BUILDER.EXECUTIONS_LABEL') }}
                  </span>
                  <span
                    v-if="automation.stats.success_rate !== null"
                    class="flex items-center gap-1 font-medium min-w-0 break-words"
                    :class="
                      automation.stats.success_rate >= 80
                        ? 'text-n-teal-11'
                        : automation.stats.success_rate >= 50
                          ? 'text-n-amber-11'
                          : 'text-n-ruby-11'
                    "
                  >
                    <span
                      class="i-lucide-check-circle size-3.5"
                      aria-hidden="true"
                    />
                    {{ automation.stats.success_rate }}%
                    {{ t('PIPELINE.FLOW_BUILDER.SUCCESS_LABEL') }}
                  </span>
                  <span
                    v-if="automation.stats.last_executed_at"
                    class="flex items-center gap-1 min-w-0 break-words sm:ml-auto"
                  >
                    <span class="i-lucide-clock size-3.5" aria-hidden="true" />
                    {{ relativeTime(automation.stats.last_executed_at) }}
                  </span>
                </div>

                <!-- Stats row 2: avg time + sparkline -->
                <div class="flex items-center justify-between">
                  <span
                    v-if="automation.stats.avg_execution_time_ms"
                    class="text-xs text-n-slate-9"
                  >
                    {{ formatAvgTime(automation.stats.avg_execution_time_ms) }}
                  </span>
                  <!-- Last 7 runs sparkline -->
                  <div
                    v-if="automation.stats.last_7_runs?.length"
                    class="flex items-center gap-0.5 ml-auto"
                    :title="t('PIPELINE.FLOW_BUILDER.LAST_RUNS_TITLE')"
                  >
                    <div
                      v-for="(run, i) in automation.stats.last_7_runs"
                      :key="i"
                      class="w-2 h-2 rounded-full"
                      :class="{
                        'bg-n-teal-9': run === 'completed',
                        'bg-n-ruby-9': run === 'failed',
                        'bg-n-slate-5': run === 'running' || run === 'pending',
                      }"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div
              data-testid="automation-actions"
              class="flex flex-wrap items-center gap-2 pt-1"
            >
              <Button
                v-if="userIsAdmin"
                variant="ghost"
                color="slate"
                size="sm"
                class="min-h-11 basis-full sm:basis-auto sm:flex-1"
                @click="emit('edit', automation.id)"
              >
                <template #icon>
                  <span class="i-lucide-pencil size-3.5" />
                </template>
                {{ t('PIPELINE.FLOW_BUILDER.EDIT_FLOW') }}
              </Button>
              <Button
                variant="ghost"
                color="slate"
                size="sm"
                class="min-h-11 min-w-11"
                :title="t('PIPELINE.FLOW_BUILDER.VIEW_HISTORY')"
                :aria-label="t('PIPELINE.FLOW_BUILDER.VIEW_HISTORY')"
                @click="emit('history', automation.id)"
              >
                <template #icon>
                  <span class="i-lucide-history size-4" />
                </template>
              </Button>
              <Button
                v-if="userIsAdmin"
                variant="ghost"
                color="slate"
                size="sm"
                class="min-h-11 min-w-11"
                :disabled="duplicatingId === automation.id"
                :title="t('PIPELINE.FLOW_BUILDER.DUPLICATE')"
                :aria-label="t('PIPELINE.FLOW_BUILDER.DUPLICATE')"
                @click="handleDuplicate(automation)"
              >
                <template #icon>
                  <span
                    :class="
                      duplicatingId === automation.id
                        ? 'i-lucide-loader-2 animate-spin'
                        : 'i-lucide-copy'
                    "
                    class="size-4"
                  />
                </template>
              </Button>
              <Button
                v-if="userIsAdmin"
                variant="ghost"
                color="slate"
                size="sm"
                class="min-h-11 min-w-11"
                :disabled="exportingId === automation.id"
                :title="t('PIPELINE.FLOW_BUILDER.EXPORT')"
                :aria-label="t('PIPELINE.FLOW_BUILDER.EXPORT')"
                @click="handleExport(automation)"
              >
                <template #icon>
                  <span class="i-lucide-download size-4" />
                </template>
              </Button>
              <Button
                v-if="userIsAdmin"
                variant="ghost"
                color="ruby"
                size="sm"
                class="min-h-11 min-w-11"
                :title="t('PIPELINE.FLOW_BUILDER.DELETE_ARIA')"
                :aria-label="t('PIPELINE.FLOW_BUILDER.DELETE_ARIA')"
                @click="handleDelete(automation)"
              >
                <template #icon>
                  <FluentIcon icon="delete" size="16" :icons="dashboardIcons" />
                </template>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Modal
      :show="Boolean(automationToDelete)"
      initial-focus-selector="[data-delete-automation-cancel]"
      @close="dismissDelete"
    >
      <section class="p-4 sm:p-6">
        <h3 data-modal-title class="mb-2 text-heading-3 text-n-slate-12">
          {{ t('PIPELINE.AUTOMATIONS.DELETE.TITLE') }}
        </h3>
        <p class="mb-4 text-sm text-n-slate-11">
          {{
            t('PIPELINE.AUTOMATIONS.DELETE.DESCRIPTION', {
              name: automationToDelete.name,
            })
          }}
        </p>
        <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            color="slate"
            size="lg"
            data-delete-automation-cancel
            class="w-full sm:w-auto"
            @click="dismissDelete"
          >
            {{ t('PIPELINE.FLOW_BUILDER.CANCEL_KEEP_EDITING') }}
          </Button>
          <Button
            type="button"
            variant="solid"
            color="ruby"
            size="lg"
            class="w-full sm:w-auto"
            @click="performDelete"
          >
            {{ t('PIPELINE.AUTOMATIONS.DELETE.CONFIRM') }}
          </Button>
        </div>
      </section>
    </Modal>
  </div>
</template>
